const API_BASE_URL =
  import.meta.env.VITE_AUTH_API_URL || 'http://localhost:3005/api/v1'

export interface FeatureAdoptionLimit {
  limitName: string
  limitDisplayName: string
  limitValue: number
  tierBaselineValue: number
  additionalMonthlyCost: number
}

export interface FeatureAdoptionOrg {
  organizationId: string
  organizationName: string
  subscriptionTierName: string | null
  isTierDefault: boolean
  additionalMonthlyCost: number
  enabledSince: string
  limits: FeatureAdoptionLimit[]
}

export interface EnhancedApiAccount {
  oauthAccountId: string
  platformAccountId: string | null
  platformName: string | null
  platformDisplayName: string | null
  username: string | null
  displayName: string | null
  providerUsername: string | null
  provider: string
  connectedAt: string
  tokenExpiresAt: string | null
  tokenRefreshedAt: string | null
  tokenStatus: string
  /**
   * ig_graph_api_connected on customer_platform_accounts. False alongside an
   * active OAuth row means enrichment will skip the account.
   */
  platformAccountFlagged: boolean
}

export interface EnhancedApiOrg {
  organizationId: string
  organizationName: string
  customerId: string | null
  customerName: string | null
  subscriptionTierName: string | null
  connectedAccountCount: number
  firstConnectedAt: string
  accounts: EnhancedApiAccount[]
}

export interface Organization {
  id: string
  organizationName: string
  customerId: string
  subscriptionStatus: string
  /**
   * Subscription tier - supports both legacy string format (production)
   * and new object format (dev environment) for backward compatibility
   */
  subscriptionTier: string | SubscriptionTier
  subscriptionTierId?: number
  subscriptionStartsAt?: string
  subscriptionExpiresAt?: string
  customLimits?: Record<string, any> | null
  billingCustomerId?: string | null
  isActive: boolean
  createdAt: string
  updatedAt: string
  logoUrlDisplay?: string | null
  logoUrlThumbnail?: string | null
  logoUrlOriginal?: string | null
  subscriptionTierDetails?: {
    id: number
    tierName: string
    tierDisplayName: string
    features: Record<string, boolean>
    limits: Record<string, number>
    pricing: {
      monthly_price: number
      annual_price: number
      annual_discount_percent: number
    }
  }
}

export interface Customer {
  id: string
  customerName: string
  customerType: string
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface OrganizationListResponse {
  success: boolean
  data: {
    organizations: Organization[]
    pagination: {
      page: number
      limit: number
      total: number
    }
  }
}

export interface OrganizationDetailsResponse {
  success: boolean
  data: Organization
}

export interface CustomerResponse {
  success: boolean
  data: Customer
}

export interface CreateOrganizationRequest {
  organizationName: string
}

export interface CreateOrganizationResponse {
  success: boolean
  data: {
    organizationId: string
    customerId: string
    organizationName: string
    message: string
  }
}

export interface UpdateOrganizationRequest {
  organizationName?: string
  subscriptionTierId?: number
  subscriptionStatus?: 'active' | 'trial' | 'expired' | 'cancelled'
  subscriptionStartsAt?: string
  subscriptionExpiresAt?: string
  customLimits?: Record<string, any>
  billingCustomerId?: string
  isActive?: boolean
}

export interface UpdateCustomerRequest {
  customerName?: string
  customerType?: string
  isActive?: boolean
}

export interface SubscriptionTier {
  id: number
  tierName: string
  tierDisplayName: string
  features: Record<string, boolean>
  limits: Record<string, number>
  pricing: {
    annual_price: number
    monthly_price: number
    annual_discount_percent: number
  }
  isActive: boolean
}

export interface SubscriptionTiersResponse {
  success: boolean
  data: SubscriptionTier[]
}

export interface ApiResponse {
  success: boolean
  message?: string
  error?: {
    code: string
    message: string
    field?: string
  }
}

// Organization Features Types (from API spec)
export interface OrganizationFeature {
  id: string
  organizationId: string
  featureId?: string
  featureName: string
  featureDisplayName?: string
  featureDescription?: string
  isEnabled: boolean
  validFrom: Date | string
  validUntil?: Date | string | null
  changedBy: string | null
  changeReason: string | null
  isTierDefault: boolean
  additionalMonthlyCost: number
  createdAt?: Date | string
}

// Master Feature Type (from API spec)
export interface Feature {
  id: string
  name: string
  displayName: string
  description: string | null
  isActive: boolean
  createdAt: Date | string
  updatedAt: Date | string
}

export interface GetAllFeaturesResponse {
  success: true
  data: Feature[]
}

export interface GetFeaturesResponse {
  success: true
  data: OrganizationFeature[]
}

export interface AddFeatureRequest {
  featureId: string
  isEnabled: boolean
  changeReason: string
  additionalCost?: number
}

export interface AddFeatureResponse {
  success: true
  message: string
  data: {
    recordId: string
    featureId: string
    isEnabled: boolean
    additionalCost: number
    wasExisting: boolean
  }
}

export interface UpdateFeatureRequest {
  isEnabled: boolean
  changeReason: string
  additionalCost?: number
}

export interface UpdateFeatureResponse {
  success: true
  message: string
  data: {
    recordId: string
    featureId: string
    isEnabled: boolean
    additionalCost: number
  }
}

export interface GetFeatureHistoryResponse {
  success: true
  data: OrganizationFeature[]
}

export interface GetBillingResponse {
  success: true
  data: {
    baseMonthlyFee: number
    additionalFeatureCosts: number
    totalMonthlyFee: number
    features: Array<{
      featureName: string
      additionalMonthlyCost: number
    }>
  }
}

export type ErrorCode =
  | 'VALIDATION_ERROR'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'FEATURE_NOT_FOUND'
  | 'LIMIT_NOT_FOUND'
  | 'INTERNAL_ERROR'
  | 'UNAUTHORIZED'

export interface ApiErrorResponse {
  success: false
  error: {
    code: ErrorCode
    message: string
    field?: string
    details?: string
  }
}

export type ApiFeatureResponse<T> = T | ApiErrorResponse

// Organization Limits Types (from API spec)
export interface OrganizationLimit {
  id: string
  organizationId: string
  limitId?: string
  limitName: string
  limitDisplayName?: string
  limitDescription?: string
  limitValue: number
  validFrom: Date | string
  validUntil?: Date | string | null
  changedBy: string | null
  changeReason: string | null
  tierBaselineValue: number
  additionalMonthlyCost: number
  createdAt?: Date | string
}

export interface UserCapacity {
  currentUsers: number
  maxUsers: number
  canAddUser: boolean
  remainingSlots: number
}

export interface Limit {
  id: string
  name: string
  displayName: string
  description: string | null
  isActive: boolean
  createdAt: Date | string
  updatedAt: Date | string
}

export interface GetLimitsResponse {
  success: true
  data: OrganizationLimit[]
}

export interface GetAllLimitsResponse {
  success: true
  data: Limit[]
}

export interface GetLimitHistoryResponse {
  success: true
  data: OrganizationLimit[]
}

export interface AddLimitRequest {
  limitId: string
  limitValue: number
  changeReason: string
  additionalCost?: number
}

export interface AddLimitResponse {
  success: true
  message: string
  data: {
    recordId: string
    limitId: string
    limitValue: number
    additionalCost: number
    wasExisting: boolean
  }
}

export interface UpdateLimitRequest {
  limitValue: number
  changeReason: string
  additionalCost?: number
  limitName?: string
}

export interface UpdateLimitResponse {
  success: true
  message: string
  data: {
    recordId: string
    limitId: string
    limitValue: number
    additionalCost: number
  }
}

export interface CheckUserCapacityResponse {
  success: true
  data: UserCapacity
}

export interface GetLimitBillingResponse {
  success: true
  data: {
    baseMonthlyFee: number
    additionalLimitCosts: number
    limits: Array<{
      limitName: string
      limitValue: number
      tierBaselineValue: number
      additionalMonthlyCost: number
    }>
  }
}

export type ApiLimitResponse<T> = T | ApiErrorResponse

export interface OrganizationListParams {
  page?: number
  limit?: number
  search?: string
  customerId?: string
  isActive?: boolean
}

export interface Address {
  id: string
  organizationId: string
  addressLine1: string
  addressLine2?: string | null
  addressLine3?: string | null
  locality: string
  region: string
  postalCode: string
  countryCode: string
  addressTypeId: number
  isPrimary: boolean
  validFrom: string
  validUntil?: string | null
  createdBy: string
  notes?: string | null
  createdAt: string
  updatedAt: string
  addressType: string
  addressTypeDescription: string
}

export interface AddressesResponse {
  success: boolean
  data: Address[]
}

export interface CreateAddressRequest {
  addressLine1: string
  addressLine2?: string | null
  addressLine3?: string | null
  locality: string
  region: string
  postalCode: string
  countryCode: string
  addressTypeId: number
  isPrimary?: boolean
  notes?: string | null
}

export interface UpdateAddressRequest {
  addressLine1?: string
  addressLine2?: string | null
  addressLine3?: string | null
  locality?: string
  region?: string
  postalCode?: string
  countryCode?: string
  isPrimary?: boolean
  notes?: string | null
}

class OrganizationService {
  async getOrganizations(
    params: OrganizationListParams = {}
  ): Promise<OrganizationListResponse> {
    const searchParams = new URLSearchParams()

    if (params.page) searchParams.append('page', params.page.toString())
    if (params.limit) searchParams.append('limit', params.limit.toString())
    if (params.search) searchParams.append('search', params.search)
    if (params.customerId) searchParams.append('customerId', params.customerId)
    if (params.isActive !== undefined)
      searchParams.append('isActive', params.isActive.toString())

    const queryString = searchParams.toString()
    const url = `${API_BASE_URL}/organizations${
      queryString ? `?${queryString}` : ''
    }`

    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      }
    })

    return await response.json()
  }

  async getOrganization(id: string): Promise<OrganizationDetailsResponse> {
    const response = await fetch(`${API_BASE_URL}/organizations/${id}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      }
    })

    return await response.json()
  }

  async createOrganization(
    data: CreateOrganizationRequest
  ): Promise<CreateOrganizationResponse> {
    const response = await fetch(`${API_BASE_URL}/organizations`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })

    return await response.json()
  }

  async updateOrganization(
    id: string,
    data: UpdateOrganizationRequest
  ): Promise<ApiResponse> {
    const response = await fetch(`${API_BASE_URL}/organizations/${id}`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })

    return await response.json()
  }

  async getCustomer(organizationId: string): Promise<CustomerResponse> {
    const response = await fetch(
      `${API_BASE_URL}/organizations/${organizationId}/customer`,
      {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      }
    )

    return await response.json()
  }

  async updateCustomer(
    organizationId: string,
    data: UpdateCustomerRequest
  ): Promise<ApiResponse> {
    const response = await fetch(
      `${API_BASE_URL}/organizations/${organizationId}/customer`,
      {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      }
    )

    return await response.json()
  }

  async getSubscriptionTiers(): Promise<SubscriptionTiersResponse> {
    const response = await fetch(`${API_BASE_URL}/organizations/tiers`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      }
    })

    return await response.json()
  }

  async getAddresses(
    organizationId: string,
    primaryOnly?: boolean
  ): Promise<AddressesResponse> {
    const params = new URLSearchParams()
    if (primaryOnly !== undefined)
      params.append('primaryOnly', primaryOnly.toString())

    const queryString = params.toString()
    const url = `${API_BASE_URL}/organizations/${organizationId}/addresses${
      queryString ? `?${queryString}` : ''
    }`

    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      }
    })

    return await response.json()
  }

  async createAddress(
    organizationId: string,
    data: CreateAddressRequest
  ): Promise<ApiResponse> {
    const response = await fetch(
      `${API_BASE_URL}/organizations/${organizationId}/addresses`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      }
    )

    return await response.json()
  }

  async updateAddress(
    organizationId: string,
    addressId: string,
    data: UpdateAddressRequest
  ): Promise<ApiResponse> {
    const response = await fetch(
      `${API_BASE_URL}/organizations/${organizationId}/addresses/${addressId}`,
      {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      }
    )

    return await response.json()
  }

  // Organization Features Methods
  // GET /api/v1/organizations/:orgId/features
  async getOrganizationFeatures(
    organizationId: string
  ): Promise<ApiFeatureResponse<GetFeaturesResponse>> {
    const response = await fetch(
      `${API_BASE_URL}/organizations/${organizationId}/features`,
      {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      }
    )
    return await response.json()
  }

  // POST /api/v1/organizations/:orgId/features
  async addOrganizationFeature(
    organizationId: string,
    featureId: string,
    isEnabled: boolean,
    changeReason: string,
    additionalCost?: number
  ): Promise<ApiFeatureResponse<AddFeatureResponse>> {
    const requestBody: AddFeatureRequest = {
      featureId,
      isEnabled,
      changeReason,
      additionalCost: additionalCost || 0
    }

    const response = await fetch(
      `${API_BASE_URL}/organizations/${organizationId}/features`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      }
    )
    return await response.json()
  }

  // PUT /api/v1/organizations/:orgId/features/:featureId
  // Note: Requires changeReason parameter according to API spec
  async updateOrganizationFeature(
    organizationId: string,
    featureId: string,
    isEnabled: boolean,
    changeReason: string,
    additionalCost?: number
  ): Promise<ApiFeatureResponse<UpdateFeatureResponse>> {
    const requestBody: UpdateFeatureRequest = {
      isEnabled,
      changeReason,
      additionalCost: additionalCost || 0
    }

    const response = await fetch(
      `${API_BASE_URL}/organizations/${organizationId}/features/${featureId}`,
      {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      }
    )
    return await response.json()
  }

  // GET /api/v1/organizations/:orgId/features/history
  async getFeatureHistory(
    organizationId: string,
    featureId?: string
  ): Promise<ApiFeatureResponse<GetFeatureHistoryResponse>> {
    const query = featureId ? `?featureId=${featureId}` : ''
    const response = await fetch(
      `${API_BASE_URL}/organizations/${organizationId}/features/history${query}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      }
    )
    return await response.json()
  }

  // GET /api/v1/organizations/:orgId/features/billing
  async getFeatureBilling(
    organizationId: string
  ): Promise<ApiFeatureResponse<GetBillingResponse>> {
    const response = await fetch(
      `${API_BASE_URL}/organizations/${organizationId}/features/billing`,
      {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      }
    )
    return await response.json()
  }

  // GET /api/v1/features - Master Data Endpoints
  async getAvailableFeatures(): Promise<
    ApiFeatureResponse<GetAllFeaturesResponse>
  > {
    const response = await fetch(`${API_BASE_URL}/features`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' }
    })
    return await response.json()
  }

  // GET /api/v1/features/adoption
  async getFeatureAdoption(featureName: string): Promise<{
    success: boolean
    data: {
      featureName: string
      featureDisplayName: string | null
      totalOrganizations: number
      organizations: FeatureAdoptionOrg[]
    }
  }> {
    const response = await fetch(
      `${API_BASE_URL}/features/adoption?featureName=${encodeURIComponent(featureName)}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      }
    )
    return await response.json()
  }

  // GET /api/v1/features/adoption/enhanced-api
  async getEnhancedApiAdoption(): Promise<{
    success: boolean
    data: {
      totalOrganizations: number
      totalAccounts: number
      organizations: EnhancedApiOrg[]
    }
  }> {
    const response = await fetch(
      `${API_BASE_URL}/features/adoption/enhanced-api`,
      {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      }
    )
    return await response.json()
  }

  // Organization Limits Methods
  // GET /api/v1/organizations/:orgId/limits
  async getOrganizationLimits(
    organizationId: string
  ): Promise<ApiLimitResponse<GetLimitsResponse>> {
    const response = await fetch(
      `${API_BASE_URL}/organizations/${organizationId}/limits`,
      {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      }
    )
    return await response.json()
  }

  // GET /api/v1/organizations/:orgId/limits/history
  async getLimitHistory(
    organizationId: string,
    limitId?: string
  ): Promise<ApiLimitResponse<GetLimitHistoryResponse>> {
    const query = limitId ? `?limitId=${limitId}` : ''
    const response = await fetch(
      `${API_BASE_URL}/organizations/${organizationId}/limits/history${query}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      }
    )
    return await response.json()
  }

  // POST /api/v1/organizations/:orgId/limits
  async addOrganizationLimit(
    organizationId: string,
    limitId: string,
    limitValue: number,
    changeReason: string,
    additionalCost?: number
  ): Promise<ApiLimitResponse<AddLimitResponse>> {
    const requestBody: AddLimitRequest = {
      limitId,
      limitValue,
      changeReason,
      additionalCost: additionalCost || 0
    }

    const response = await fetch(
      `${API_BASE_URL}/organizations/${organizationId}/limits`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      }
    )
    return await response.json()
  }

  // PUT /api/v1/organizations/:orgId/limits/:limitId
  async updateOrganizationLimit(
    organizationId: string,
    limitId: string,
    limitValue: number,
    changeReason: string,
    additionalCost?: number,
    limitName?: string
  ): Promise<ApiLimitResponse<UpdateLimitResponse>> {
    const requestBody: UpdateLimitRequest = {
      limitValue,
      changeReason,
      additionalCost: additionalCost || 0,
      limitName
    }

    const response = await fetch(
      `${API_BASE_URL}/organizations/${organizationId}/limits/${limitId}`,
      {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      }
    )
    return await response.json()
  }

  // GET /api/v1/organizations/:orgId/limits/check-user-capacity
  async checkUserCapacity(
    organizationId: string
  ): Promise<ApiLimitResponse<CheckUserCapacityResponse>> {
    const response = await fetch(
      `${API_BASE_URL}/organizations/${organizationId}/limits/check-user-capacity`,
      {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      }
    )
    return await response.json()
  }

  // GET /api/v1/organizations/:orgId/limits/billing
  async getLimitBilling(
    organizationId: string
  ): Promise<ApiLimitResponse<GetLimitBillingResponse>> {
    const response = await fetch(
      `${API_BASE_URL}/organizations/${organizationId}/limits/billing`,
      {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      }
    )
    return await response.json()
  }

  // GET /api/v1/limits - Master Data Endpoints
  async getAvailableLimits(): Promise<ApiLimitResponse<GetAllLimitsResponse>> {
    const response = await fetch(`${API_BASE_URL}/limits`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' }
    })
    return await response.json()
  }

  // PATCH /api/v1/organizations/:orgId/complete-first-time-setup
  async completeFirstTimeSetup(organizationId: string): Promise<ApiResponse> {
    const response = await fetch(
      `${API_BASE_URL}/organizations/${organizationId}/complete-first-time-setup`,
      {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      }
    )
    return await response.json()
  }

  // POST /api/v1/organizations/:orgId/logo - Upload organization logo
  async uploadLogo(
    organizationId: string,
    file: File
  ): Promise<{
    success: boolean
    data?: { logoUrl: string }
    error?: { message: string }
  }> {
    const formData = new FormData()
    formData.append('logo', file)

    const response = await fetch(
      `${API_BASE_URL}/organizations/${organizationId}/logo`,
      {
        method: 'POST',
        credentials: 'include',
        body: formData
      }
    )

    return response.json()
  }

  // DELETE /api/v1/organizations/:orgId/logo - Delete organization logo
  async deleteLogo(
    organizationId: string
  ): Promise<{ success: boolean; error?: { message: string } }> {
    const response = await fetch(
      `${API_BASE_URL}/organizations/${organizationId}/logo`,
      {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      }
    )

    return response.json()
  }
}

export const organizationService = new OrganizationService()
